import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { useFiscalDeLaEmpresa } from '../sesion/fiscal';

/** Un tercero como lo devuelven la lista y la ficha: los dos perfiles, estén o no. */
export interface Tercero {
  readonly id: string;
  readonly tipoDeIdentificacion: string;
  readonly identificacion: string | null;
  readonly razonSocial: string;
  readonly nombreComercial: string | null;
  readonly condicionIva: string;
  readonly ciudad: string | null;
  readonly telefono: string | null;
  readonly correo: string | null;
  readonly activo: boolean;
  readonly esProveedor: boolean;
  readonly diasDePago: number | null;
  readonly monedaDelProveedor: string | null;
  readonly esCliente: boolean;
  readonly limiteDeCredito: string | null;
  readonly diasDePagoDelCliente: number | null;
  /** La lista con la que compra como cliente, si tiene una asignada (E36). */
  readonly listaDePreciosDelCliente?: string | null;
}

/**
 * Proveedores: a quién le compra el comercio.
 *
 * Es la pantalla de terceros filtrada por perfil. Se llama "Proveedores" y no "Terceros" porque
 * nadie en un mostrador dice "voy a dar de alta un tercero": dice que va a cargar un proveedor.
 * El modelo sabe que son perfiles de una misma identidad; la pantalla habla como el comercio.
 *
 * La identificación se escribe como está en la factura, con guiones, y el servidor la guarda en
 * dígitos. Pedirle a alguien que la escriba de una forma distinta a como la tiene delante es la
 * manera más segura de que la escriba mal.
 */
export function Proveedores(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeCrear = usePermiso('TERCEROS.TERCERO.CREAR');

  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fiscal = useFiscalDeLaEmpresa();
  const [tipoDeIdentificacion, setTipo] = useState(fiscal.tiposDeTercero[0] ?? 'SIN_IDENTIFICAR');
  const [identificacion, setIdentificacion] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [nombreComercial, setNombreComercial] = useState('');
  const [condicionIva, setCondicion] = useState<string>(
    fiscal.condicionesDeTercero[0] ?? 'consumidor_final',
  );
  const [diasDePago, setDiasDePago] = useState('30');
  const [telefono, setTelefono] = useState('');
  const [correo, setCorreo] = useState('');

  const consulta = useQuery({
    queryKey: ['terceros', busqueda],
    queryFn: () =>
      pedir<Tercero[]>(
        `/terceros?soloProveedores=true${
          busqueda.trim() === '' ? '' : `&texto=${encodeURIComponent(busqueda.trim())}`
        }`,
      ),
  });

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/terceros', {
        method: 'POST',
        cuerpo: {
          tipoDeIdentificacion,
          ...(identificacion.trim() === '' ? {} : { identificacion: identificacion.trim() }),
          razonSocial: razonSocial.trim(),
          ...(nombreComercial.trim() === '' ? {} : { nombreComercial: nombreComercial.trim() }),
          condicionIva,
          ...(telefono.trim() === '' ? {} : { telefono: telefono.trim() }),
          ...(correo.trim() === '' ? {} : { correo: correo.trim() }),
          proveedor: { diasDePago: Number(diasDePago) || 0 },
        },
      }),
    onSuccess: async () => {
      setAbierto(false);
      setIdentificacion('');
      setRazonSocial('');
      setNombreComercial('');
      setTelefono('');
      setCorreo('');
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['terceros'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const columnas: Columna<Tercero>[] = [
    {
      clave: 'razonSocial',
      titulo: t('compras.razonSocial'),
      contenido: (fila) => (
        <>
          {fila.razonSocial}
          {fila.nombreComercial === null ? null : (
            <span className="block text-xs text-texto-tenue">{fila.nombreComercial}</span>
          )}
        </>
      ),
    },
    {
      clave: 'identificacion',
      titulo: t('compras.identificacion'),
      contenido: (fila) => (
        <span className="numero">
          {fila.identificacion ?? '—'}
          <span className="block text-xs text-texto-tenue">
            {fiscal.nombreDeTipo(fila.tipoDeIdentificacion)}
          </span>
        </span>
      ),
    },
    {
      clave: 'condicionIva',
      titulo: t('compras.condicionIva'),
      contenido: (fila) => fiscal.nombreDeCondicion(fila.condicionIva),
    },
    {
      clave: 'diasDePago',
      titulo: t('compras.diasDePago'),
      numerica: true,
      contenido: (fila) => <span className="numero">{fila.diasDePago ?? '—'}</span>,
    },
    {
      clave: 'moneda',
      titulo: t('compras.monedaDelProveedor'),
      contenido: (fila) => fila.monedaDelProveedor ?? '—',
    },
    {
      clave: 'contacto',
      titulo: t('compras.telefono'),
      contenido: (fila) => fila.telefono ?? fila.correo ?? '—',
    },
    {
      clave: 'estado',
      titulo: t('compras.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activo ? 'bien' : 'neutro'}>
          {fila.activo ? t('sucursales.activa') : t('sucursales.inactiva')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      // Gestionar, no borrar: un tercero con movimientos no se puede borrar sin dejar una cuenta
      // corriente apuntando a la nada. La ficha además muestra el circuito de compra entero.
      contenido: (fila) => (
        <div className="flex justify-end">
          <Link to="/proveedores/$id" params={{ id: fila.id }}>
            <Boton tono="principal">{t('ficha.gestionar')}</Boton>
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('compras.proveedores')}
        descripcion={t('compras.descripcionProveedores')}
        acciones={
          puedeCrear ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAbierto(true);
                setError(null);
              }}
            >
              {t('compras.nuevoProveedor')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <div className="mb-3 max-w-sm">
          <Campo
            etiqueta={t('compras.buscarProveedor')}
            type="search"
            value={busqueda}
            onChange={(evento) => {
              setBusqueda(evento.target.value);
            }}
          />
        </div>

        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio descripcion={t('compras.sinProveedores')} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('compras.nuevoProveedor')}
        onCerrar={() => {
          setAbierto(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={razonSocial.trim() === ''}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('compras.razonSocial')}
            obligatorio
            maxLength={200}
            value={razonSocial}
            onChange={(evento) => {
              setRazonSocial(evento.target.value);
            }}
          />
          <Campo
            etiqueta={t('compras.nombreComercial')}
            maxLength={160}
            value={nombreComercial}
            ayuda={t('compras.ayudaNombreComercial')}
            onChange={(evento) => {
              setNombreComercial(evento.target.value);
            }}
          />

          <div className="grid grid-cols-2 gap-3">
            <Selector
              etiqueta={t('compras.tipoDeIdentificacion')}
              value={tipoDeIdentificacion}
              onChange={(evento) => {
                setTipo(evento.target.value);
              }}
            >
              {fiscal.tiposDeTercero.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {fiscal.nombreDeTipo(tipo)}
                </option>
              ))}
            </Selector>
            <Campo
              etiqueta={t('compras.identificacion')}
              value={identificacion}
              maxLength={32}
              disabled={tipoDeIdentificacion === 'SIN_IDENTIFICAR'}
              ayuda={t('compras.ayudaIdentificacion')}
              onChange={(evento) => {
                setIdentificacion(evento.target.value);
              }}
            />
          </div>

          <Selector
            etiqueta={t('compras.condicionIva')}
            value={condicionIva}
            onChange={(evento) => {
              setCondicion(evento.target.value);
            }}
          >
            {fiscal.condicionesDeTercero.map((condicion) => (
              <option key={condicion} value={condicion}>
                {fiscal.nombreDeCondicion(condicion)}
              </option>
            ))}
          </Selector>

          <div className="grid grid-cols-3 gap-3">
            <Campo
              etiqueta={t('compras.diasDePago')}
              inputMode="numeric"
              value={diasDePago}
              ayuda={t('compras.ayudaDiasDePago')}
              onChange={(evento) => {
                setDiasDePago(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('compras.telefono')}
              maxLength={40}
              value={telefono}
              onChange={(evento) => {
                setTelefono(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('compras.correo')}
              type="email"
              maxLength={160}
              value={correo}
              onChange={(evento) => {
                setCorreo(evento.target.value);
              }}
            />
          </div>

          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
